import { ReactNode } from 'react'
import { Routes, Route, useParams } from 'react-router-dom'
import { EventScopeProvider } from '@labrav/common'
import { FundingBlockPage } from './components/FundingBlockPage/FundingBlockPage'
import { FundingDashboard } from './components/FundingDashboard/FundingDashboard'
import { FundingRequestDetail } from './components/FundingRequestDetail/FundingRequestDetail'
import { FundingDataProps } from './types'

/**
 * Scopes a request's events to the request itself. Sits above the page rather
 * than inside it: a component cannot read a scope it renders, so anything
 * tracked from the page's own handlers would otherwise miss all of this.
 */
const FundingRequestScope = ({ children }: { children: ReactNode }) => {
  const { program, workflowId } = useParams<{
    program: string
    workflowId: string
  }>()

  return (
    <EventScopeProvider
      metadata={{
        route_key: 'funding_request_detail',
        // The URL carries the program exactly as the funding API names it; lower
        // it here so one program can't split into two values in the analytics.
        workflow_key: program?.toLowerCase(),
        funding_request_id: workflowId,
      }}
    >
      {children}
    </EventScopeProvider>
  )
}

export interface FundingAppProps extends FundingDataProps {
  isFundingEnabled?: boolean
  onHeaderChange?: (header: ReactNode | null) => void
}

export function FundingApp({
  isFundingEnabled = false,
  isFundingSettingsEnabled = false,
  onHeaderChange,
  ...data
}: FundingAppProps) {
  if (!isFundingEnabled) return <FundingBlockPage />

  const dataProps: FundingDataProps = {
    ...data,
    isFundingSettingsEnabled,
  }

  return (
    <EventScopeProvider
      metadata={{ product: 'funding', account_id: data.accountId }}
    >
      <Routes>
        <Route
          index
          element={
            <EventScopeProvider metadata={{ route_key: 'funding_dashboard' }}>
              <FundingDashboard {...dataProps} />
            </EventScopeProvider>
          }
        />
        <Route
          path="requests/:program/:workflowId"
          element={
            <FundingRequestScope>
              <FundingRequestDetail
                {...dataProps}
                onHeaderChange={onHeaderChange}
              />
            </FundingRequestScope>
          }
        />
      </Routes>
    </EventScopeProvider>
  )
}
